import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusBadge } from '@/components/base/StatCard';
import { customers, type Customer, type CustomerStatus } from '@/mocks/customers';

const allStatuses: CustomerStatus[] = ["Aktif", "Pasif", "Sorunlu", "Yasaklı"];

const genderOptions = ["Tümü", "Kadın", "Erkek"];
const sourceOptions = ["Tümü", "Google", "Meta", "WhatsApp", "Tavsiye", "Tekrar Müşteri"];

export default function MusterilerPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [genderFilter, setGenderFilter] = useState('Tümü');
  const [sourceFilter, setSourceFilter] = useState('Tümü');
  const [sortBy, setSortBy] = useState<'name' | 'visits' | 'spent' | 'lastVisit'>('name');
  const [visitFilter, setVisitFilter] = useState<'all' | 'last1month' | 'last3months' | 'custom'>('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [customerList, setCustomerList] = useState<Customer[]>(customers);
  const [showAddModal, setShowAddModal] = useState(false);
  const [toast, setToast] = useState<{message: string; type: 'success' | 'error'} | null>(null);

  const [newForm, setNewForm] = useState({
    fullName: '',
    phone: '',
    email: '',
    gender: 'Erkek' as 'Kadın' | 'Erkek',
    birthDate: '',
    source: 'Google' as Customer['source'],
    address: '',
    privateNotes: '',
    satisfactionNote: '',
    status: 'Aktif' as CustomerStatus,
  });

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleAddCustomer = () => {
    if (!newForm.fullName.trim() || !newForm.phone.trim()) {
      showToast('Lütfen ad soyad ve telefon girin', 'error');
      return;
    }
    const newCustomer: Customer = {
      id: `cus-${Date.now()}`,
      fullName: newForm.fullName.trim(),
      phone: newForm.phone.trim(),
      email: newForm.email.trim() || '',
      gender: newForm.gender,
      birthDate: newForm.birthDate || '',
      source: newForm.source,
      status: newForm.status,
      totalVisits: 0,
      lastVisitDate: '',
      totalSpent: 0,
      openPayment: 0,
      activePackage: false,
      remainingSessions: 0,
      satisfactionNote: newForm.satisfactionNote.trim(),
      privateNotes: newForm.privateNotes.trim(),
      address: newForm.address.trim(),
      createdAt: new Date().toISOString().split('T')[0],
    };
    setCustomerList(prev => [newCustomer, ...prev]);
    setShowAddModal(false);
    setNewForm({
      fullName: '', phone: '', email: '', gender: 'Erkek', birthDate: '', source: 'Google',
      address: '', privateNotes: '', satisfactionNote: '', status: 'Aktif',
    });
    showToast('Müşteri başarıyla eklendi');
  };

  const filtered = useMemo(() => {
    let data = [...customerList];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(c =>
        c.fullName.toLowerCase().includes(q) ||
        c.phone.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') data = data.filter(c => c.status === statusFilter);
    if (genderFilter !== 'Tümü') data = data.filter(c => c.gender === genderFilter);
    if (sourceFilter !== 'Tümü') data = data.filter(c => c.source === sourceFilter);

    // Ziyaret filtresi (son gelme tarihi)
    if (visitFilter === 'last1month') {
      const cutoff = '2026-04-08';
      data = data.filter(c => c.lastVisitDate >= cutoff);
    }
    if (visitFilter === 'last3months') {
      const cutoff = '2026-02-08';
      data = data.filter(c => c.lastVisitDate >= cutoff);
    }
    if (visitFilter === 'custom') {
      if (customStartDate) data = data.filter(c => c.lastVisitDate >= customStartDate);
      if (customEndDate) data = data.filter(c => c.lastVisitDate <= customEndDate);
    }

    data.sort((a, b) => {
      if (sortBy === 'name') return a.fullName.localeCompare(b.fullName);
      if (sortBy === 'visits') return b.totalVisits - a.totalVisits;
      if (sortBy === 'spent') return b.totalSpent - a.totalSpent;
      if (sortBy === 'lastVisit') return (b.lastVisitDate || '').localeCompare(a.lastVisitDate || '');
      return 0;
    });

    return data;
  }, [customerList, searchQuery, statusFilter, genderFilter, sourceFilter, sortBy, visitFilter, customStartDate, customEndDate]);

  const stats = useMemo(() => {
    return {
      total: filtered.length,
      active: filtered.filter(c => c.status === 'Aktif').length,
      withOpenPayment: filtered.filter(c => c.openPayment > 0).length,
      withPackage: filtered.filter(c => c.activePackage).length,
      totalRevenue: filtered.reduce((sum, c) => sum + c.totalSpent, 0),
    };
  }, [filtered]);

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>
            Müşteriler
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
            {stats.total} müşteri kaydı
          </p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="spa-btn-primary text-sm flex items-center gap-2 self-start sm:self-auto whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
          Yeni Müşteri
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Toplam', value: stats.total, icon: 'ri-user-smile-line', color: '#C9A962' },
          { label: 'Aktif', value: stats.active, icon: 'ri-user-follow-line', color: '#10B981' },
          { label: 'Açık Ödeme', value: stats.withOpenPayment, icon: 'ri-error-warning-line', color: '#EF4444' },
          { label: 'Paketli', value: stats.withPackage, icon: 'ri-vip-crown-line', color: '#8B5CF6' },
        ].map(s => (
          <div key={s.label} className="spa-card p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-spa flex items-center justify-center flex-shrink-0" style={{ background: s.color + '12' }}>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${s.icon} text-lg`} style={{ color: s.color }} />
              </div>
            </div>
            <div>
              <p className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>{s.value}</p>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-sm" style={{ color: 'var(--text-muted)' }} />
          </div>
          <input
            className="spa-input pl-10"
            placeholder="İsim, telefon veya e-posta ara..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <select className="spa-input text-sm min-w-[100px]" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">Tüm Durumlar</option>
            {allStatuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="spa-input text-sm min-w-[100px]" value={genderFilter} onChange={e => setGenderFilter(e.target.value)}>
            {genderOptions.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <select className="spa-input text-sm min-w-[100px]" value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
            {sourceOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="spa-input text-sm min-w-[100px]" value={sortBy} onChange={e => setSortBy(e.target.value as typeof sortBy)}>
            <option value="name">İsme Göre</option>
            <option value="visits">Ziyaret Sayısı</option>
            <option value="spent">Harcmaya Göre</option>
            <option value="lastVisit">Son Ziyaret</option>
          </select>
        </div>
      </div>

      {/* Ziyaret Filtreleri */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Ziyaret:</span>
        {[
          { key: 'all' as const, label: 'Tümü' },
          { key: 'last1month' as const, label: 'Son 1 Ay' },
          { key: 'last3months' as const, label: 'Son 3 Ay' },
          { key: 'custom' as const, label: 'Tarih Aralığı' },
        ].map(v => (
          <button
            key={v.key}
            onClick={() => setVisitFilter(v.key)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              visitFilter === v.key ? 'text-white' : ''
            }`}
            style={visitFilter === v.key
              ? { background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }
              : { background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }
            }
          >
            {v.label}
          </button>
        ))}
        {visitFilter === 'custom' && (
          <div className="flex items-center gap-2 ml-2">
            <input
              type="date"
              className="spa-input text-xs py-1 min-w-[120px]"
              value={customStartDate}
              onChange={e => setCustomStartDate(e.target.value)}
            />
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>-</span>
            <input
              type="date"
              className="spa-input text-xs py-1 min-w-[120px]"
              value={customEndDate}
              onChange={e => setCustomEndDate(e.target.value)}
            />
          </div>
        )}
      </div>

      {/* Customer Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map(customer => (
          <div
            key={customer.id}
            onClick={() => navigate(`/musteri/${customer.id}`)}
            className="spa-card p-4 cursor-pointer group"
          >
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-semibold text-white flex-shrink-0"
                style={{ background: customer.status === 'Sorunlu' ? '#EF4444' : 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }}>
                {customer.fullName.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-sm truncate group-hover:text-[var(--gold)] transition-colors" style={{ color: 'var(--text-primary)' }}>
                    {customer.fullName}
                  </p>
                  {customer.status === 'Sorunlu' && (
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-fill text-sm" style={{ color: '#EF4444' }} />
                    </div>
                  )}
                </div>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{customer.phone}</p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <StatusBadge status={customer.status}>{customer.status}</StatusBadge>
                  {customer.activePackage && (
                    <span className="spa-badge" style={{ background: 'rgba(139,92,246,0.12)', color: '#8B5CF6' }}>
                      {customer.remainingSessions} seans
                    </span>
                  )}
                  {customer.openPayment > 0 && (
                    <span className="spa-badge" style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444' }}>
                      ₺{customer.openPayment.toLocaleString('tr-TR')}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <div className="text-center">
                <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{customer.totalVisits}</p>
                <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Ziyaret</p>
              </div>
              <div className="text-center">
                <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>₺{customer.totalSpent.toLocaleString('tr-TR')}</p>
                <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Toplam</p>
              </div>
              <div className="text-center">
                <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{customer.lastVisitDate ? new Date(customer.lastVisitDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }) : '-'}</p>
                <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Son Geliş</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-3 pt-3 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                <div className="w-3 h-3 flex items-center justify-center"><i className="ri-map-pin-line text-xs" /></div>
                {customer.source}
              </div>
              <div className="flex items-center gap-1 text-xs ml-auto" style={{ color: 'var(--text-muted)' }}>
                <div className="w-3 h-3 flex items-center justify-center"><i className="ri-calendar-line text-xs" /></div>
                {customer.createdAt ? new Date(customer.createdAt).toLocaleDateString('tr-TR') : '-'}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 space-y-3">
          <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
            <i className="ri-user-search-line text-xl" style={{ color: 'var(--text-muted)' }} />
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Müşteri bulunamadı</p>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-4">
          <div className="absolute inset-0" style={{ background: 'var(--modal-overlay)' }} onClick={() => setShowAddModal(false)} />
          <div className="relative bg-theme-card rounded-spa w-full max-w-lg flex flex-col max-h-[90vh] animate-scale-in border overflow-hidden" style={{ borderColor: 'var(--card-border)' }}>
            <div className="flex items-center justify-between p-5 flex-shrink-0">
              <h3 className="text-lg font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Yeni Müşteri Ekle</h3>
              <button onClick={() => setShowAddModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="px-5 pb-5 space-y-3 text-sm overflow-y-auto flex-1 min-h-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Ad Soyad *</label>
                  <input
                    className="spa-input w-full"
                    placeholder="Müşteri adı soyadı"
                    value={newForm.fullName}
                    onChange={e => setNewForm(prev => ({ ...prev, fullName: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Telefon *</label>
                  <input
                    className="spa-input w-full"
                    placeholder="+90 5XX XXX XXXX"
                    value={newForm.phone}
                    onChange={e => setNewForm(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>E-posta</label>
                  <input
                    type="email"
                    className="spa-input w-full"
                    placeholder="ornek@email.com"
                    value={newForm.email}
                    onChange={e => setNewForm(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Cinsiyet</label>
                  <select
                    className="spa-input w-full"
                    value={newForm.gender}
                    onChange={e => setNewForm(prev => ({ ...prev, gender: e.target.value as 'Kadın' | 'Erkek' }))}
                  >
                    <option value="Kadın">Kadın</option>
                    <option value="Erkek">Erkek</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Doğum Tarihi</label>
                  <input
                    type="date"
                    className="spa-input w-full"
                    value={newForm.birthDate}
                    onChange={e => setNewForm(prev => ({ ...prev, birthDate: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Kaynak</label>
                  <select
                    className="spa-input w-full"
                    value={newForm.source}
                    onChange={e => setNewForm(prev => ({ ...prev, source: e.target.value as Customer['source'] }))}
                  >
                    {['Google', 'Meta', 'WhatsApp', 'Tavsiye', 'Tekrar Müşteri'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Adres</label>
                <input
                  className="spa-input w-full"
                  placeholder="İlçe, İl"
                  value={newForm.address}
                  onChange={e => setNewForm(prev => ({ ...prev, address: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Özel Notlar</label>
                <textarea
                  className="spa-input w-full min-h-[60px] resize-none"
                  maxLength={500}
                  placeholder="Personel için gizli notlar..."
                  value={newForm.privateNotes}
                  onChange={e => setNewForm(prev => ({ ...prev, privateNotes: e.target.value }))}
                />
                <p className="text-[10px] text-right mt-1" style={{ color: 'var(--text-muted)' }}>{newForm.privateNotes.length}/500</p>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Memnuniyet Notu</label>
                <textarea
                  className="spa-input w-full min-h-[60px] resize-none"
                  maxLength={500}
                  placeholder="Müşteri memnuniyeti ile ilgili not..."
                  value={newForm.satisfactionNote}
                  onChange={e => setNewForm(prev => ({ ...prev, satisfactionNote: e.target.value }))}
                />
                <p className="text-[10px] text-right mt-1" style={{ color: 'var(--text-muted)' }}>{newForm.satisfactionNote.length}/500</p>
              </div>
            </div>

            <div className="flex gap-2 p-5 pt-2 flex-shrink-0">
              <button onClick={() => setShowAddModal(false)} className="flex-1 spa-btn-secondary text-sm py-2.5">
                İptal
              </button>
              <button onClick={handleAddCustomer} className="flex-1 spa-btn-primary text-sm py-2.5">
                <i className="ri-check-line mr-1" />Müşteri Ekle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[200] animate-fade-in">
          <div
            className="px-4 py-3 rounded-spa-sm text-sm font-medium flex items-center gap-2"
            style={{
              background: toast.type === 'error' ? 'rgba(239,68,68,0.95)' : 'rgba(16,185,129,0.95)',
              color: '#fff',
              backdropFilter: 'blur(12px)',
            }}
          >
            <i className={toast.type === 'error' ? 'ri-error-warning-line' : 'ri-checkbox-circle-line'} />
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
}